import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowUpstreamDatasourcesFilteredByFlowOutputFieldQuery,
  FlowUpstreamDatasourcesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamDatasourcesConnection_nodes,
  FlowUpstreamDatasourcesFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamDatasourcesFilteredByFlowOutputFieldQuery';
import {
  FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery,
  // tslint:disable-next-line: max-line-length
  FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_upstreamDatasourcesConnection_nodes,
  FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery';
import {
  FlowUpstreamDatasourcesQuery,
  FlowUpstreamDatasourcesQuery_flows_upstreamDatasourcesConnection_nodes,
  FlowUpstreamDatasourcesQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamDatasourcesQuery';
import {
  FlowUpstreamDatasourcesWithoutPersonalSpaceQuery,
  FlowUpstreamDatasourcesWithoutPersonalSpaceQuery_flows_upstreamDatasourcesConnection_nodes,
  FlowUpstreamDatasourcesWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamDatasourcesWithoutPersonalSpaceQuery';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { FlowUpstreamDatasourcesFilteredByFlowOutputFieldQueryGql } from '../../queries/FlowUpstreamDatasourcesFilteredByFlowOutputFieldQueryGql';
import { FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql } from '../../queries/FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql';
import { FlowUpstreamDatasourcesQueryGql } from '../../queries/FlowUpstreamDatasourcesQueryGql';
import { FlowUpstreamDatasourcesWithoutPersonalSpaceQueryGql } from '../../queries/FlowUpstreamDatasourcesWithoutPersonalSpaceQueryGql';
import { DatasourcesDisplayGrid, DatasourcesDisplayGridRowData } from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = FlowUpstreamDatasourcesQueryVariables;
type QueryResults = FlowUpstreamDatasourcesQuery;
type QueryResultListNode = FlowUpstreamDatasourcesQuery_flows_upstreamDatasourcesConnection_nodes;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = FlowUpstreamDatasourcesWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = FlowUpstreamDatasourcesWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowUpstreamDatasourcesWithoutPersonalSpaceQuery_flows_upstreamDatasourcesConnection_nodes;

type QueryVariablesWithFilter = FlowUpstreamDatasourcesFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowUpstreamDatasourcesFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowUpstreamDatasourcesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamDatasourcesConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery;
// tslint:disable-next-line: max-line-length
type QueryResultListNodeWithFilterWithoutPersonalSpace = FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_upstreamDatasourcesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamDatasourcesConnection : null;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamDatasourcesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamDatasourcesConnection : null;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamDatasourcesConnection : null;
}

function mapResultNodeWithoutPersonalSpace(node: QueryResultListNodeWithoutPersonalSpace): GridRowData {
  return {
    id: node.id,
    name: node.name,
    description: node.description || '',
    owner: node.owner,
    workbookCount: node.downstreamWorkbooksConnection ? node.downstreamWorkbooksConnection.totalCount : null,
    vizportalUrlId: node.vizportalUrlId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    extractLastUpdateTime: node.extractLastUpdateTime || null,
    containerName: node.projectName,
    containerType: 'project',
    projectVizportalUrlId: node.projectVizportalUrlId,
    hasExtracts: !!node.hasExtracts,
    isCertified: node.isCertified,
    certifier: node.certifier,
    certificationNote: node.certificationNote || ''
  };
}

function mapResultNode(node: QueryResultListNode): GridRowData {
  return {
    id: node.id,
    name: node.name,
    description: node.description || '',
    owner: node.owner,
    workbookCount: node.downstreamWorkbooksConnection ? node.downstreamWorkbooksConnection.totalCount : null,
    vizportalUrlId: node.vizportalUrlId,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    extractLastUpdateTime: node.extractLastUpdateTime || null,
    containerName: node.containerName,
    containerType: node.containerType,
    projectVizportalUrlId: node.projectVizportalUrlId,
    hasExtracts: !!node.hasExtracts,
    isCertified: node.isCertified,
    certifier: node.certifier,
    certificationNote: node.certificationNote || ''
  };
}

export type FlowUpstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowUpstreamDatasourcesQueryGrid(props: FlowUpstreamDatasourcesQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatasourcesLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
        queryGql={FlowUpstreamDatasourcesFilteredByFlowOutputFieldQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={mapResultNode}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowUpstreamDatasourcesQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={mapResultNode}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
        QueryResultListNodeWithFilterWithoutPersonalSpace, GridRowData>
        queryGql={FlowUpstreamDatasourcesFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={mapResultNodeWithoutPersonalSpace}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
        QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowUpstreamDatasourcesWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as PublishedDatasourceSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={mapResultNodeWithoutPersonalSpace}
      >
        {({ rowStore }) => <DatasourcesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  );
}
